import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Search,
  AlertCircle,
  Loader2,
  X,
  Eye
} from 'lucide-react';
import axios from 'axios';
import { getSectionPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Inventory = () => {
  const navigate = useNavigate();
  const perms = getSectionPerms('inventory');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/inventory`, getAuthHeaders());
      setInventory(response.data);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.product_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.brand_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="inventory-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inventory</h1>
          <p className="text-slate-600 mt-1">View stock levels and pricing by product</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-sm">
            <span className="font-bold">{inventory.length}</span> Products in Stock
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by product name, code, brand or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Inventory Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-sm border border-slate-200">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : filteredInventory.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 text-center py-16" data-testid="empty-state">
          <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No inventory data</h3>
          <p className="text-slate-500 mb-6">Add inward entries to see inventory here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredInventory.map((item) => (
            <div
              key={item.id}
              data-testid={`inventory-card-${item.id}`}
              onClick={() => navigate(`/admin/inventory/${item.product_id}`)}
              className="bg-white rounded-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            >
              {/* Product Image */}
              <div className="relative aspect-[4/3] bg-slate-100">
                {item.product_image ? (
                  <img
                    src={item.product_image}
                    alt={item.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="h-16 w-16 text-slate-300" />
                  </div>
                )}
                
                {/* Stock Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${
                    item.quantity > 10 
                      ? 'bg-green-500 text-white' 
                      : item.quantity > 0 
                        ? 'bg-yellow-500 text-white'
                        : 'bg-red-500 text-white'
                  }`}>
                    {item.quantity} {item.unit_name || 'units'}
                  </span>
                </div>

                {/* Tax Badge */}
                {item.tax_percentage && (
                  <div className="absolute top-3 right-3">
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-black/60 text-white">
                      +{item.tax_percentage}%
                    </span>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-4">
                {/* Product Code */}
                {item.product_code && (
                  <p className="text-xs font-mono text-slate-400 mb-1">{item.product_code}</p>
                )}
                
                {/* Product Name */}
                <h3 className="font-semibold text-slate-900 text-lg mb-2 line-clamp-2">{item.product_name}</h3>
                
                {/* Brand & Category */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {item.brand_name && (
                    <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      {item.brand_name}
                    </span>
                  )}
                  {item.category_name && (
                    <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                      {item.category_name}
                    </span>
                  )}
                  {item.company_name && (
                    <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                      {item.company_name}
                    </span>
                  )}
                </div>

                {/* Pricing */}
                {!perms.hidePrices && (
                <div className="pt-3 border-t border-slate-100 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Purchase:</span>
                    <span className="text-sm text-slate-600">₹{item.purchase_rate?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Sale:</span>
                    <span className="text-sm text-slate-600">₹{item.sale_rate?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                    <span className="text-sm font-medium text-slate-700">Display Price:</span>
                    <span className="text-xl font-bold text-orange-600">₹{item.display_rate?.toLocaleString()}</span>
                  </div>
                </div>
                )}
                {perms.hidePrices && (
                <div className="pt-3 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-700">Display Price:</span>
                    <span className="text-xl font-bold text-orange-600">₹{item.display_rate?.toLocaleString()}</span>
                  </div>
                </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
